import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import axios from 'axios';
import { message } from 'antd';
import RequirementsExportModal from './requirements-ui/RequirementsExportModal';
import VerificationHistoryPanel from './compliance-ui/VerificationHistoryPanel';
import PdfViewerModal from './PdfViewerModal';
import { createPdfReferenceHandlers } from '../utils/openPdfReference';
import { inferOriginalPdfKeyFromDocumentId } from './compliance-ui/complianceIssuePresenters';
import { useAuth } from '../auth.js';
import { phRegisterPopulated, phTourRegisterViewed, phRequirementSearched, phCitationClicked } from '../utils/posthogEvents';
import ZoneProfilePanel from './ZoneProfilePanel.jsx';
import {
  getProjectZoneProfiles,
  getCachedPendingZoneProfile,
  clearCachedPendingZoneProfile,
} from '../services/zoneProfileService.js';
const API_BASE = process.env.REACT_APP_API_BASE_REQUIREMENTS || 'http://localhost:8000';

const COMPLIANCE_TABS = [
  { key: 'ALL',                 label: 'All' },
  { key: 'BUILDING_CODE',       label: 'Building Code' },
  { key: 'CLIENT_REQUIREMENTS', label: 'Client Requirements' },
  { key: 'ZONING',              label: 'Zoning' },
  { key: 'STANDARDS',           label: 'Standards' },
];

const COMPLIANCE_LABELS = {
  BUILDING_CODE:       'Building Code',
  CLIENT_REQUIREMENTS: 'Client Req.',
  ZONING:              'Zoning',
  STANDARDS:           'Standards',
};

const SUBCATEGORIES = {
  BUILDING_CODE: [
    { value: 'LIFE_SAFETY',              label: 'Life Safety' },
    { value: 'STRUCTURAL',               label: 'Structural' },
    { value: 'MECHANICAL',               label: 'Mechanical' },
    { value: 'ELECTRICAL',               label: 'Electrical' },
    { value: 'PLUMBING',                 label: 'Plumbing' },
    { value: 'ACCESSIBILITY',            label: 'Accessibility' },
    { value: 'ENERGY_AND_SUSTAINABILITY',label: 'Energy & Sustainability' },
  ],
  STANDARDS: [
    { value: 'ACCESSIBILITY',            label: 'Accessibility' },
    { value: 'STRUCTURAL',               label: 'Structural' },
    { value: 'MECHANICAL_AND_PLUMBING',  label: 'Mechanical & Plumbing' },
    { value: 'FIRE_AND_LIFE_SAFETY',     label: 'Fire & Life Safety' },
    { value: 'ENVELOPE',                 label: 'Envelope' },
  ],
};

const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
`;

const spinAnim = keyframes`to { transform: rotate(360deg); }`;
const fadeInAnim = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - var(--topnav-height, 90px));
  flex: 1;
  overflow: hidden;
  background: #fafafa;
  font-family: 'Poppins', sans-serif;
  color: #333333;
  font-size: 0.9rem;
  line-height: 1.6;
`;

/* ─────────────────────────────────────────
   PAGE HEADER  (.page-header)
───────────────────────────────────────── */
const PageHeader = styled.div`
  padding: 2rem 2rem 1.25rem;
  background: #ffffff;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const PageHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 500;
  color: #333333;
  letter-spacing: -0.3px;
  margin: 0;
`;

const PageSubtitle = styled.p`
  font-size: 0.85rem;
  color: #999999;
  margin: 0.25rem 0 0;
`;

const ExtractingBanner = styled.div`
  margin: 1rem 2rem 0;
  background: linear-gradient(135deg, rgba(233,112,36,0.06), rgba(233,112,36,0.02));
  border: 1px solid rgba(233,112,36,0.2);
  border-radius: 10px;
  padding: 0.875rem 1.125rem;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  font-size: 0.82rem;
  flex-shrink: 0;
  animation: ${fadeInAnim} 0.25s ease;
`;

const BannerSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(233,112,36,0.15);
  border-top-color: #e97024;
  border-radius: 50%;
  animation: ${spinAnim} 0.8s linear infinite;
  flex-shrink: 0;
`;

const BannerText = styled.div`
  flex: 1;
  color: #374151;
  font-weight: 500;
  line-height: 1.4;

  span {
    display: block;
    font-size: 0.75rem;
    color: #6b7280;
    font-weight: 400;
    margin-top: 0.1rem;
  }
`;


const ResumeBanner = styled.div`
  margin: 1rem 2rem 0;
  background: linear-gradient(135deg, rgba(8,145,178,0.08), rgba(8,145,178,0.02));
  border: 1px solid rgba(8,145,178,0.28);
  border-radius: 10px;
  padding: 0.875rem 1.125rem;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  font-size: 0.82rem;
  flex-shrink: 0;
  animation: ${fadeInAnim} 0.25s ease;
`;

const ResumeIcon = styled.div`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: #0891b2;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ResumeButton = styled.button`
  margin-left: auto;
  flex-shrink: 0;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: none;
  background: #e97024;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s ease;
  &:hover { background: #d4621a; }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 1rem;
  margin: 1.25rem 2rem 0;
  flex-shrink: 0;
`;

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: ${p => p.$total
    ? 'linear-gradient(135deg, #e97024 0%, #c45e18 100%)'
    : '#ffffff'};
  border: 1px solid ${p => p.$total ? 'transparent' : p.$active ? 'rgba(233,112,36,0.4)' : '#f0f0f0'};
  border-radius: 10px;
  padding: 0.875rem 1.125rem;
  flex: 1;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${p => p.$active && !p.$total
    ? '0 2px 10px rgba(233,112,36,0.12)'
    : '0 2px 8px rgba(0,0,0,0.06)'};
  position: relative;
  overflow: hidden;

  &:hover {
    box-shadow: ${p => p.$total
      ? '0 4px 16px rgba(233,112,36,0.35)'
      : '0 4px 12px rgba(0,0,0,0.12)'};
    border-color: ${p => p.$total ? 'transparent' : 'rgba(233,112,36,0.25)'};
    transform: translateY(-1px);
  }
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 500;
  line-height: 1;
  color: ${p => p.$total ? '#ffffff' : '#333333'};
`;

const StatLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${p => p.$total ? 'rgba(255,255,255,0.8)' : '#999999'};
  margin-top: 0.375rem;
`;

const StatBar = styled.div`
  width: 100%;
  height: 3px;
  background: ${p => p.$total ? 'rgba(255,255,255,0.3)' : '#e8e8e8'};
  border-radius: 2px;
  margin-top: 0.625rem;
  overflow: hidden;
`;

const StatBarFill = styled.div`
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
  width: ${p => p.$pct || 0}%;
  background: ${p =>
    p.$total                              ? 'rgba(255,255,255,0.9)' :
    p.$type === 'BUILDING_CODE'           ? '#7c3aed' :
    p.$type === 'CLIENT_REQUIREMENTS'     ? '#e97024' :
    p.$type === 'ZONING'                  ? '#0891b2' :
    p.$type === 'STANDARDS'               ? '#2563eb' :
    '#e97024'};
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin: 1rem 2rem 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  flex-wrap: wrap;
  flex-shrink: 0;
`;

const FilterLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 300;
  color: #000000;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-right: 0.25rem;
  white-space: nowrap;
`;

const FilterDivider = styled.div`
  width: 1px;
  height: 20px;
  background: #e0e0e0;
  margin: 0 0.25rem;
`;

const SearchWrapper = styled.div`
  position: relative;

  svg {
    position: absolute;
    left: 0.6rem;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    pointer-events: none;
  }
`;

const SearchInput = styled.input`
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 0.4rem 0.75rem 0.4rem 2rem;
  font-size: 0.82rem;
  color: #333333;
  width: 220px;
  transition: all 0.2s ease;
  font-family: 'Poppins', inherit;
  outline: none;

  &::placeholder { color: #999999; }
  &:focus {
    background: white;
    box-shadow: 0 0 0 2px rgba(233,112,36,0.2);
    border-color: #e97024;
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
`;

const DropdownTrigger = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  background: #f5f5f5;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 0.3rem 0.625rem;
  font-size: 0.82rem;
  color: #333333;
  font-family: 'Poppins', inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  user-select: none;

  &:hover, &[data-open="true"] {
    background: white;
    border-color: #e97024;
    box-shadow: 0 0 0 2px rgba(233,112,36,0.2);
  }
`;

const DropdownArrow = styled.span`
  color: #999;
  font-size: 10px;
  display: inline-block;
  transition: transform 0.2s;
  transform: ${p => p.$open ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 100%;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.12);
  z-index: 200;
  overflow: hidden;
  display: ${p => p.$open ? 'block' : 'none'};
  animation: ${fadeInAnim} 0.15s ease;
`;

const DropdownItem = styled.div`
  padding: 0.5rem 0.875rem;
  font-size: 0.82rem;
  color: ${p => p.$selected ? '#e97024' : '#333333'};
  font-weight: ${p => p.$selected ? '500' : '400'};
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s ease;

  &:hover { background: #fafafa; }
`;


const BodyLayout = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  margin-top: 1.25rem;
`;

const TableArea = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  padding: 0 1rem 1.25rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const TableContainer = styled.div`
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);

  flex: 1;
  min-height: 0;
  overflow: auto; 
`;

const TableHead = styled.div`
  display: grid;
   grid-template-columns: ${p => p.$showSub
    ? '150px 1fr 155px 165px 195px 100px 64px'
    : '150px 1fr 155px 195px 100px 64px'};
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const Th = styled.div`
  padding: 0.75rem 1rem;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #999999;
  white-space: nowrap;
  user-select: none;
`;

const ReqRow = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$showSub
    ? '150px 1fr 155px 165px 195px 100px 64px'
    : '150px 1fr 155px 195px 100px 64px'};
  align-items: start;
  border-bottom: 1px solid #f0f0f0;
  cursor: default;
  transition: background 0.12s ease;

  &:last-child { border-bottom: none; }
  &:hover { background: #fff7f1; }
`;

const TdRef = styled.div`
  padding: 0.75rem 1rem;
  font-family: 'Courier New', monospace;
  font-size: 0.78rem;
  font-weight: 700;
  color: #666666;
  white-space: normal;
  overflow-wrap: break-word;
  line-height: 1.4;
`;

const TdText = styled.div`
  padding: 0.75rem 1rem;
  color: #333333;
  line-height: 1.55;
  font-size: 0.85rem;
`;

const TdTextInner = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TdType = styled.div`
  padding: 0.75rem 1rem;
`;

const TdSub = styled.div`
  padding: 0.75rem 1rem;
  font-size: 0.78rem;
  color: #666666;
`;

const TdSource = styled.div`
  padding: 0.75rem 1rem;
`;

const TdAction = styled.div`
  padding: 0.75rem 0.5rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 0.25rem;
`;

const TdStatus = styled.div`
  padding: 0.75rem 0.75rem;
`;

// verification_status pill — colors per prototype .vs--* rules
const VS_BADGE_STYLES = {
  verified:       'background:rgba(76,175,80,0.15);color:#388e3c;',
  non_compliant:  'background:rgba(244,67,54,0.15);color:#d32f2f;',
  needs_review:   'background:rgba(255,193,7,0.15);color:#f57c00;',
  not_applicable: 'background:#f0f0f0;color:#666666;',
  stale:          'background:rgba(139,92,246,0.1);color:#7c3aed;',
  unverified:     'background:#fafafa;color:#999999;border:1px solid #e0e0e0;',
};
const VS_BADGE_LABELS = {
  verified: 'Verified',
  non_compliant: 'Non-Compliant',
  needs_review: 'Needs Review',
  not_applicable: 'Not Applicable',
  stale: 'Stale',
  unverified: 'Unverified',
};
const VsBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
  white-space: nowrap;
  line-height: 1.5;
  ${p => VS_BADGE_STYLES[p.$status] || VS_BADGE_STYLES.unverified}
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.3px;
  white-space: nowrap;
  background: ${p =>
    p.$type === 'BUILDING_CODE'       ? 'rgba(139,92,246,0.1)'  :
    p.$type === 'CLIENT_REQUIREMENTS' ? 'rgba(233,112,36,0.1)'  :
    p.$type === 'ZONING'              ? 'rgba(6,182,212,0.1)'   :
    p.$type === 'STANDARDS'           ? 'rgba(59,130,246,0.1)'  :
    'rgba(0,0,0,0.06)'};
  color: ${p =>
    p.$type === 'BUILDING_CODE'       ? '#7c3aed' :
    p.$type === 'CLIENT_REQUIREMENTS' ? '#e97024' :
    p.$type === 'ZONING'              ? '#0891b2' :
    p.$type === 'STANDARDS'           ? '#2563eb' :
    '#666666'};
`;


const DocPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
  font-size: 0.72rem;
  color: #312e81;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: ${p => (p.$clickable ? 'pointer' : 'default')};
  transition: all 0.15s ease;

  &:hover { background: #e0e7ff; border-color: #a5b4fc; }
  svg { flex-shrink: 0; color: #6366f1; }
`;

const DocPillPage = styled.span`
  font-weight: 700;
  margin-left: 2px;
  color: #4f46e5;
`;


const SectionDivider = styled.div`
  padding: 0.625rem 1rem;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #cccccc;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
`;

const IconBtn = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 5px;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.15s;
  opacity: 0;

  ${ReqRow}:hover & { opacity: 1; }
  &:hover { background: #f3f4f6; border-color: rgba(0,0,0,0.1); color: #374151; }
`;


const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1.25rem;
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-top: 1rem;
  flex-shrink: 0;
`;

const PaginationInfo = styled.div`
  font-size: 0.82rem;
  color: #999999;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

const PageBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid ${p => p.$active ? '#e97024' : '#e0e0e0'};
  background: ${p => p.$active ? '#e97024' : '#ffffff'};
  color: ${p => p.$active ? '#ffffff' : '#666666'};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 500;
  font-family: 'Poppins', inherit;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${p => p.$active ? '#e97024' : '#f5f5f5'};
    color: ${p => p.$active ? '#fff' : '#333'};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const PageBtnWide = styled(PageBtn)`
  width: auto;
  padding: 0 0.75rem;
  font-size: 0.78rem;
`;


const SourcePanel = styled.div`
  width: ${p => p.$collapsed ? '40px' : '260px'};
  margin-right: 2rem;
  border-radius: 10px;
  flex-shrink: 0;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: width 0.3s ease;
  align-self: stretch;
  max-height: 100%;
`;

const SourcePanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${p => p.$collapsed ? 'center' : 'space-between'};
  padding: ${p => p.$collapsed ? '1rem 0' : '1rem 1rem 0.875rem'};
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
`;

const SourcePanelTitle = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #000000;
  white-space: nowrap;
  overflow: hidden;
  opacity: ${p => p.$collapsed ? 0 : 1};
  transition: opacity 0.2s;
`;

const PanelCollapseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #999999;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover { background: #f5f5f5; color: #333; }
`;

const SourcePanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem 0;
  display: ${p => p.$collapsed ? 'none' : 'block'};

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 2px; }
`;

const SourceSectionLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #cccccc;
  padding: 0 1rem 0.375rem;
  margin-top: 0.5rem;
`;

const SourceAllBtn = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 0.375rem;
  background: ${p => p.$active ? 'rgba(233,112,36,0.06)' : 'transparent'};
  transition: background 0.15s ease;

  &:hover { background: ${p => p.$active ? 'rgba(233,112,36,0.06)' : '#fafafa'}; }
`;

const SourceAllLabel = styled.span`
  font-size: 0.82rem;
  font-weight: ${p => p.$active ? '600' : '500'};
  color: ${p => p.$active ? '#e97024' : '#333333'};
  flex: 1;
`;

const SourceAllCount = styled.span`
  font-size: 0.68rem;
  font-weight: 600;
  background: ${p => p.$active ? 'rgba(233,112,36,0.12)' : '#f0f0f0'};
  border-radius: 9px;
  padding: 0.1rem 0.45rem;
  color: ${p => p.$active ? '#e97024' : '#666666'};
`;

const SourceDocItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.625rem 1rem;
  cursor: pointer;
  background: ${p => p.$active ? 'rgba(233,112,36,0.06)' : 'transparent'};
  transition: background 0.15s ease;

  &:hover { background: ${p => p.$active ? 'rgba(233,112,36,0.06)' : '#fafafa'}; }
`;

const SourceDocIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${p => p.$active ? 'rgba(233,112,36,0.1)' : '#f0f0f0'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${p => p.$active ? '#e97024' : '#999999'};
`;

const SourceDocInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SourceDocName = styled.div`
  font-size: 0.8rem;
  color: ${p => p.$active ? '#e97024' : '#333333'};
  font-weight: ${p => p.$active ? '600' : '500'};
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SourceDocMeta = styled.div`
  font-size: 0.7rem;
  color: #999999;
  margin-top: 0.1rem;
`;

const SourceDocCount = styled.span`
  font-size: 0.68rem;
  font-weight: 600;
  color: ${p => p.$active ? '#e97024' : '#999999'};
  background: ${p => p.$active ? 'rgba(233,112,36,0.12)' : '#f0f0f0'};
  border-radius: 9px;
  padding: 0.1rem 0.45rem;
  flex-shrink: 0;
  align-self: center;
`;

const CenterWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  gap: 0.875rem;
  color: #6b7280;
  animation: ${fadeInAnim} 0.25s ease;
`;

const SpinnerEl = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid rgba(233,112,36,0.15);
  border-top-color: #e97024;
  border-radius: 50%;
  animation: ${spinAnim} 0.8s linear infinite;
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
`;

const EmptyTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
`;

const EmptySubtitle = styled.div`
  font-size: 0.8rem;
  color: #9ca3af;
  text-align: center;
  max-width: 320px;
  line-height: 1.5;
`;

const NoProjectWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 1rem;
  color: #6b7280;
  font-size: 0.875rem;
  padding: 4rem 2rem;
`;

const ExportBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.875rem;
  background: #e97024;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 0.82rem;
  font-weight: 500;
  font-family: 'Poppins', inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(233,112,36,0.25);

  &:hover {
    background: #000000;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(0,0,0,0.2);
  }
`;

function getAuthHeaders() {
  const idToken = localStorage.getItem('id_token');
  return idToken ? { Authorization: `Bearer ${idToken}` } : {};
}

function complianceLabelFull(type) {
  switch (type) {
    case 'BUILDING_CODE':       return 'Building Code';
    case 'CLIENT_REQUIREMENTS': return 'Client Requirements';
    case 'ZONING':              return 'Zoning';
    case 'STANDARDS':           return 'Standards';
    default:                    return type;
  }
}

// Parse a ref like "10.5.30(3)(B)(i)" into ordered tokens for natural sorting.
// Numbers compare numerically; letters/romans sort after numbers at the same depth.
function parseSectionKey(ref) {
  const raw = (ref || '').trim();
  if (!raw) return [];
  const tokens = raw.match(/[A-Za-z]+|\d+/g) || [];
  return tokens.map(t =>
    /^\d+$/.test(t)
      ? { n: parseInt(t, 10), s: '' }
      : { n: Number.POSITIVE_INFINITY, s: t.toLowerCase() }
  );
}

// Order requirements by section so 1.5.1 < 1.5.7 < 2.1.1 < 10.5.x and same-section
// items stay contiguous. Falls back to sectionHeader when article is absent.
function compareSection(a, b) {
  const ka = parseSectionKey(a.article || a.sectionHeader);
  const kb = parseSectionKey(b.article || b.sectionHeader);
  const len = Math.max(ka.length, kb.length);
  for (let i = 0; i < len; i++) {
    const ta = ka[i];
    const tb = kb[i];
    if (!ta) return -1; // shorter (parent) ref sorts first
    if (!tb) return 1;
    if (ta.n !== tb.n) return ta.n - tb.n;
    if (ta.s !== tb.s) return ta.s < tb.s ? -1 : 1;
  }
  return 0;
}

function groupBySection(items) {
  const groups = [];
  let lastSection = null;
  for (const item of items) {
    const section = item.sectionHeader || '';
    if (section && section !== lastSection) {
      groups.push({ type: 'divider', label: section });
      lastSection = section;
    }
    groups.push({ type: 'item', data: item });
  }
  return groups;
}


export default function ExtractedRequirementsPage({ selectedProject }) {
  const [activeTab,        setActiveTab]        = useState('ALL');
  const [searchInput,      setSearchInput]      = useState('');
  const [searchTerm,       setSearchTerm]       = useState('');
  const [items,            setItems]            = useState([]);
  const [tabCounts,        setTabCounts]        = useState({});
  const [isLoading,        setIsLoading]        = useState(false);
  const [isSearching,      setIsSearching]      = useState(false);
  const [pageToken,        setPageToken]        = useState(null);
  const [prevTokenStack,   setPrevTokenStack]   = useState([]);
  const [currentPageNum,   setCurrentPageNum]   = useState(1);
  const [hasExtractingDocs,setHasExtractingDocs]= useState(false);

  const [hideOffZone,         setHideOffZone]         = useState(false);
  const [activeSubcategory,   setActiveSubcategory]   = useState('');
  const [activeDocFilter,     setActiveDocFilter]     = useState(null);
  const [sourcePanelCollapsed,setSourcePanelCollapsed]= useState(false);
  const [typeDropdownOpen,    setTypeDropdownOpen]    = useState(false);
  const [subDropdownOpen,     setSubDropdownOpen]     = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  // Verification history slide-out panel: holds the clicked requirement (or null)
  const [historyReq, setHistoryReq] = useState(null);
  const [sourceDocs, setSourceDocs] = useState([]);
  const [tabCountsUnfiltered, setTabCountsUnfiltered] = useState({});
  const [pdfViewer, setPdfViewer] = useState({
    open: false,
    url: '',
    initialPage: 1,
    title: '',
    highlightText: null,
  });

  const searchTimerRef = useRef(null);
  const firedRegisterPopulatedRef = useRef(new Set());
  const firedRegisterViewedRef = useRef(false);
  const projectId = selectedProject?.id;
  const auth = useAuth();

  // Zone profiles for this project. `pendingZone` (awaiting confirmation) drives
  // the resume banner; `activeZone` (confirmed) drives the re-check (refilter)
  // control. Prefer the server; fall back to the client cache for pending.
  const [pendingZone, setPendingZone]     = useState(null);
  const [activeZone, setActiveZone]       = useState(null);
  const [showZonePanel, setShowZonePanel] = useState(false);
  const [zoneMode, setZoneMode]           = useState('confirm'); // 'confirm' | 'refilter'
  const [zonePanelPayload, setZonePanelPayload] = useState(null);

  const openZonePanel = (mode, payload) => {
    setZoneMode(mode);
    setZonePanelPayload(payload);
    setShowZonePanel(true);
  };

  const loadZoneState = useCallback(async () => {
    if (!projectId) { setPendingZone(null); setActiveZone(null); return; }
    let pending = null, active = null;
    try {
      const { pending: pl, active: al } = await getProjectZoneProfiles(projectId);
      if (pl && pl.length) {
        const p = pl[0];
        pending = { projectId, requirementId: p.requirementId, linkId: p.linkId, docName: p.docName };
      }
      if (al && al.length) {
        const a = al[0];
        active = { projectId, requirementId: a.requirementId, linkId: a.linkId,
                   docName: a.docName, primaryZone: a.primaryZone };
      }
    } catch {
      // Route not wired / network — fall back to the client cache for pending.
    }
    if (!pending) pending = getCachedPendingZoneProfile(projectId);
    setPendingZone(pending);
    setActiveZone(active);
  }, [projectId]);

  useEffect(() => { loadZoneState(); }, [loadZoneState]);

  const pdfHandlers = useMemo(
    () => createPdfReferenceHandlers({ projectId, auth, onOpenViewer: setPdfViewer }),
    [projectId, auth]
  );

  const handleOpenSourceDoc = useCallback((item) => {
    const originalPdfKey =
      item.originalPdfKey ||
      item.original_pdf_key ||
      inferOriginalPdfKeyFromDocumentId(
        item.sourceDocId || item.source_doc_id,
        projectId,
        item.requirementId
      );

    if (!originalPdfKey) {
      message.error('Could not locate the source PDF for this requirement.');
      return;
    }

    pdfHandlers.openPdfPageFromDoc({
      originalPdfKey,
      pageNumber: item.pageNumber,
      title: item.sourceDocName,
      highlightText: item.requirementText,
    });

    phCitationClicked({
      projectId,
      source: 'register',
      docId: item.sourceDocId || item.source_doc_id,
      pageNumber: item.pageNumber,
      requirementId: item.requirementId,
    });
  }, [pdfHandlers, projectId]);

  useEffect(() => {
    const close = () => { setTypeDropdownOpen(false); setSubDropdownOpen(false);  };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const fetchRequirements = useCallback(async ({
    tab       = activeTab,
    token     = null,
    search    = searchTerm,
    docFilter = activeDocFilter,
    searchStartedAt = null,
  } = {}) => {
    if (!projectId) return;
    const isSearch = !!search;
    isSearch ? setIsSearching(true) : setIsLoading(true);

    try {
      const params = new URLSearchParams({ projectId, complianceType: tab });
      if (token)     params.append('pageToken', token);
      if (search)    params.append('search', search);
      if (docFilter) params.append('sourceDoc', docFilter);

      const res  = await axios.get(
        `${API_BASE}/extracted-requirements?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      const data = res.data;
      setItems(data.items || []);
      setTabCounts(data.tabCounts || {});

      const totalCount = data.tabCounts?.ALL ?? 0;
      if (totalCount > 0 && !firedRegisterPopulatedRef.current.has(projectId)) {
        firedRegisterPopulatedRef.current.add(projectId);
        let extractionDurationMs = null;
        try {
          const key = `ph_extraction_started_${projectId}`;
          const startedAt = sessionStorage.getItem(key);
          if (startedAt) {
            extractionDurationMs = Date.now() - Number(startedAt);
            sessionStorage.removeItem(key);
          }
        } catch {
          /* sessionStorage unavailable — duration just won't be reported */
        }
        phRegisterPopulated({ projectId, requirementCount: totalCount, durationMs: extractionDurationMs });
      }

      if (searchStartedAt) {
        phRequirementSearched({
          projectId,
          tab,
          queryLength: search.length,
          resultCount: data.items?.length ?? 0,
          durationMs: Date.now() - searchStartedAt,
        });
      }

      if (!docFilter) setTabCountsUnfiltered(data.tabCounts || {});
      setPageToken(data.nextPageToken || null);
      setHasExtractingDocs((data.tabCounts?.ALL || 0) === 0 && !search);
    } catch (err) {
      console.error('[ExtractedRequirementsPage] fetch error:', err);
      message.error('Failed to load requirements. Please try again.');
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  }, [projectId, activeTab, searchTerm, activeDocFilter]);

  const fetchSourceDocs = useCallback(async (tab = activeTab) => {
  if (!projectId) return;
  try {
    const params = new URLSearchParams({ projectId, complianceType: tab, sourceDocsOnly: 'true' });
    const res = await axios.get(
      `${API_BASE}/extracted-requirements?${params.toString()}`,
      { headers: getAuthHeaders() }
    );
    setSourceDocs(res.data.sourceDocs || []);
  } catch (err) {
    console.error('[ExtractedRequirementsPage] source docs fetch error:', err);
  }
}, [projectId, activeTab]);

  useEffect(() => {
    if (!projectId) return;
    setItems([]);
    setPageToken(null);
    setPrevTokenStack([]);
    setCurrentPageNum(1);
    setSearchInput('');
    setSearchTerm('');
    setActiveSubcategory('');
    setActiveDocFilter(null);
    fetchRequirements({ tab: activeTab, token: null, search: '', docFilter: null });
    fetchSourceDocs(activeTab);
  }, [projectId, activeTab]);

  /* ── search ── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (val.trim() && !firedRegisterViewedRef.current) {
      firedRegisterViewedRef.current = true;
      phTourRegisterViewed();
    }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!val.trim()) {
      setSearchTerm('');
      setPageToken(null);
      setPrevTokenStack([]);
      setCurrentPageNum(1);
      fetchRequirements({ tab: activeTab, token: null, search: '' });
      return;
    }
    searchTimerRef.current = setTimeout(() => {
      setSearchTerm(val.trim());
      setPageToken(null);
      setPrevTokenStack([]);
      setCurrentPageNum(1);
      fetchRequirements({ tab: activeTab, token: null, search: val.trim(), searchStartedAt: Date.now() });
    }, 400);
  };

  const handleDocFilterChange = (docName) => {
    const next = activeDocFilter === docName ? null : docName;
    setActiveDocFilter(next);
    setPageToken(null);
    setPrevTokenStack([]);
    setCurrentPageNum(1);
    fetchRequirements({ tab: activeTab, token: null, search: searchTerm, docFilter: next });
  };

  const handleTabChange = (key) => {
    if (key === activeTab) return;
    setActiveTab(key);
    setSearchInput('');
    setSearchTerm('');
    setPageToken(null);
    setPrevTokenStack([]);
    setCurrentPageNum(1);
    setActiveSubcategory('');
  };

  const handleNextPage = () => {
    if (!pageToken) return;
    setPrevTokenStack(prev => [...prev, pageToken]);
    setCurrentPageNum(n => n + 1);
    fetchRequirements({ tab: activeTab, token: pageToken, search: searchTerm, docFilter: activeDocFilter });
  };

  const handlePrevPage = () => {
    if (prevTokenStack.length === 0) return;
    const stack    = [...prevTokenStack];
    const prevToken = stack.pop();
    setPrevTokenStack(stack);
    setCurrentPageNum(n => n - 1);
    fetchRequirements({ tab: activeTab, token: stack.length === 0 ? null : prevToken, search: searchTerm, docFilter: activeDocFilter });
  };

  const totalCount    = tabCounts?.ALL || 0;
  const subcatOptions = SUBCATEGORIES[activeTab] || null;

  const hasOffZone = items.some(it => it.zoneApplicability === 'off_zone');

  const visibleItems = items.filter(item => {
    if (activeSubcategory && item.subcategory !== activeSubcategory && item.compliance_subcategory !== activeSubcategory) return false;
    if (hideOffZone && item.zoneApplicability === 'off_zone') return false;
    return true;
  });

  const sortedItems = [...visibleItems].sort(compareSection);
  const rows        = groupBySection(sortedItems);
  const isSearchMode = !!searchTerm;
  const showPagination = !isSearchMode && (prevTokenStack.length > 0 || !!pageToken);

  if (!projectId) {
    return (
      <>
        <GlobalStyles />
        <Page>
          <PageHeader>
            <PageHeaderLeft>
              <PageTitle>Requirements Register</PageTitle>
              <PageSubtitle>Extracted requirements from indexed documents.</PageSubtitle>
            </PageHeaderLeft>
          </PageHeader>
          <NoProjectWrap>
            <EmptyIcon>
              <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
              </svg>
            </EmptyIcon>
            <EmptyTitle>No project selected</EmptyTitle>
            <EmptySubtitle>Select a project from the top bar to view its extracted requirements.</EmptySubtitle>
          </NoProjectWrap>
        </Page>
      </>
    );
  }

  return (
    <>
      <GlobalStyles />
      <Page>

        {/* ── PAGE HEADER ── */}
        <PageHeader>
          <PageHeaderLeft>
            <PageTitle>Requirements Register</PageTitle>
            <PageSubtitle>
              {totalCount > 0
                ? `${totalCount} requirement${totalCount !== 1 ? 's' : ''} extracted`
                : 'Extracted requirements from indexed documents. Use tabs to filter by compliance type.'}
            </PageSubtitle>
          </PageHeaderLeft>
        </PageHeader>

        {/* ── EXTRACTING BANNER ── */}
        {hasExtractingDocs && !isLoading && (
          <ExtractingBanner>
            <BannerSpinner />
            <BannerText>
              Requirements extraction is in progress
              <span>Documents are still being processed. Requirements will appear here automatically as they complete.</span>
            </BannerText>
          </ExtractingBanner>
        )}

        {/* ── RESUME ZONING SETUP BANNER ── */}
        {pendingZone && (
          <ResumeBanner>
            <ResumeIcon>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
            </ResumeIcon>
            <BannerText>
              Zoning setup needs confirmation
              <span>
                {pendingZone.docName
                  ? `${pendingZone.docName} — requirements extraction is paused until you confirm.`
                  : 'Requirements extraction is paused until you confirm the decoded zoning setup.'}
              </span>
            </BannerText>
            <ResumeButton onClick={() => openZonePanel('confirm', pendingZone)}>Resume zoning setup</ResumeButton>
          </ResumeBanner>
        )}

        {/* ── STATS ROW ── */}
        <StatsRow>
          <StatCard $total onClick={() => handleTabChange('ALL')}>
            <StatValue $total>{totalCount}</StatValue>
            <StatLabel $total>Total</StatLabel>
            <StatBar $total><StatBarFill $total $pct={100} /></StatBar>
          </StatCard>

          {[
            { type: 'BUILDING_CODE',       label: 'Building Code' },
            { type: 'CLIENT_REQUIREMENTS', label: 'Client Req.'   },
            { type: 'ZONING',              label: 'Zoning'        },
            { type: 'STANDARDS',           label: 'Standards'     },
          ].map(({ type, label }) => {
            const count = tabCounts[type] || 0;
            const pct   = totalCount > 0 ? Math.round(count / totalCount * 100) : 0;
            return (
              <StatCard
                key={type}
                $active={activeTab === type}
                onClick={() => handleTabChange(type)}
              >
                <StatValue>{count}</StatValue>
                <StatLabel>{label}</StatLabel>
                <StatBar><StatBarFill $type={type} $pct={pct} /></StatBar>
              </StatCard>
            );
          })}
        </StatsRow>

        {/* ── FILTER BAR ── */}
        <FilterBar>
          <FilterLabel>Filter by</FilterLabel>

          {/* Search */}
          <SearchWrapper>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <circle cx="5.5" cy="5.5" r="4" stroke="#999" strokeWidth="1.5"/>
              <path d="M9 9l2.5 2.5" stroke="#999" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <SearchInput
              value={searchInput}
              onChange={handleSearchChange}
              {...(totalCount > 0 ? { 'data-tour': 'register-search' } : {})}
              placeholder={`Search ${activeTab === 'ALL' ? 'all' : complianceLabelFull(activeTab)} requirements…`}
            />
          </SearchWrapper>

          {hasOffZone && (
            <>
              <FilterDivider />
              <label
                title="Hide requirements scoped to a zone other than this project's"
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
                         fontSize: '0.75rem', color: '#6b7280', cursor: 'pointer',
                         whiteSpace: 'nowrap' }}
              >
                <input
                  type="checkbox"
                  checked={hideOffZone}
                  onChange={e => setHideOffZone(e.target.checked)}
                />
                Hide other-zone
              </label>
            </>
          )}

          {activeZone && (
            <>
              <FilterDivider />
              <button
                type="button"
                title={`Re-check which zoning requirements apply to your ${activeZone.primaryZone || ''} zone`}
                onClick={() => openZonePanel('refilter', activeZone)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.35rem',
                         background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                         fontSize: '0.75rem', fontWeight: 600, color: '#0891b2',
                         fontFamily: 'inherit', whiteSpace: 'nowrap' }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 4v6h-6M1 20v-6h6" />
                  <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                </svg>
                Re-check zoning{activeZone.primaryZone ? ` (${activeZone.primaryZone})` : ''}
              </button>
            </>
          )}

          <FilterDivider />

          {/* Compliance type dropdown */}
          <DropdownWrapper onClick={e => e.stopPropagation()}>
            <DropdownTrigger
              data-open={typeDropdownOpen}
              onClick={() => { setTypeDropdownOpen(o => !o); setSubDropdownOpen(false); }}
            >
              <span>
                Compliance Type: {activeTab === 'ALL' ? 'All' : (COMPLIANCE_LABELS[activeTab] || activeTab)}
              </span>
              <DropdownArrow $open={typeDropdownOpen}>▾</DropdownArrow>
            </DropdownTrigger>
            <DropdownMenu $open={typeDropdownOpen}>
              <DropdownItem $selected={activeTab === 'ALL'} onClick={() => { handleTabChange('ALL'); setTypeDropdownOpen(false); }}>
                Compliance Type: All
              </DropdownItem>
              {Object.entries(COMPLIANCE_LABELS).map(([val, lbl]) => (
                <DropdownItem
                  key={val}
                  $selected={activeTab === val}
                  onClick={() => { handleTabChange(val); setTypeDropdownOpen(false); }}
                >
                  {lbl}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </DropdownWrapper>

          {/* Subcategory dropdown */}
          {activeTab === 'BUILDING_CODE' && (
            <DropdownWrapper onClick={e => e.stopPropagation()}>
              <DropdownTrigger
                data-open={subDropdownOpen}
                onClick={() => { setSubDropdownOpen(o => !o); setTypeDropdownOpen(false); }}
              >
                <span>
                  Subcategory: {activeSubcategory
                    ? (subcatOptions.find(s => s.value === activeSubcategory)?.label || activeSubcategory)
                    : 'All'}
                </span>
                <DropdownArrow $open={subDropdownOpen}>▾</DropdownArrow>
              </DropdownTrigger>
              <DropdownMenu $open={subDropdownOpen}>
                <DropdownItem $selected={!activeSubcategory} onClick={() => { setActiveSubcategory(''); setSubDropdownOpen(false); }}>
                  Subcategory: All
                </DropdownItem>
                {subcatOptions.map(opt => (
                  <DropdownItem
                    key={opt.value}
                    $selected={activeSubcategory === opt.value}
                    onClick={() => { setActiveSubcategory(opt.value); setSubDropdownOpen(false); }}
                  >
                    {opt.label}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </DropdownWrapper>
          )}

          {isSearching && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#6b7280' }}>
              <SpinnerEl style={{ width: 14, height: 14, borderWidth: 2 }} />
              Searching…
            </div>
          )}

          <ExportBtn style={{ marginLeft: 'auto' }} onClick={() => setShowExportModal(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
            </svg>
            Export
          </ExportBtn>
        </FilterBar>

        {/* ── BODY ── */}
        <BodyLayout>

          {/* TABLE AREA */}
          <TableArea>

            {/* Loading */}
            {isLoading && (
              <CenterWrap>
                <SpinnerEl />
                <span>Loading requirements…</span>
              </CenterWrap>
            )}

            {/* Empty */}
            {!isLoading && visibleItems.length === 0 && !hasExtractingDocs && (
              <CenterWrap>
                <EmptyIcon>
                  <svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                </EmptyIcon>
                <EmptyTitle>
                  {isSearchMode
                    ? `No results for "${searchTerm}"`
                    : `No ${activeTab === 'ALL' ? '' : complianceLabelFull(activeTab) + ' '}requirements yet`}
                </EmptyTitle>
                <EmptySubtitle>
                  {isSearchMode
                    ? 'Try a different search term or switch tabs.'
                    : 'Upload and classify requirement documents from the Link Folders page to get started.'}
                </EmptySubtitle>
              </CenterWrap>
            )}

            {/* Table */}
            {!isLoading && visibleItems.length > 0 && (
              <TableContainer>
                <TableHead $showSub={activeTab === 'BUILDING_CODE'}>
                  <Th>Ref #</Th>
                  <Th>Requirement Text</Th>
                  <Th>Compliance Type</Th>
                  {activeTab === 'BUILDING_CODE' && <Th>Subcategory</Th>}
                  <Th>Source Document</Th>
                  <Th>Status</Th>
                  <Th />
                </TableHead>

                {rows.map((row, idx) => {
                  if (row.type === 'divider') {
                    return (
                      <SectionDivider key={`d-${idx}`}>
                        {row.label}
                      </SectionDivider>
                    );
                  }

                  const item = row.data;
                  const subLabel = item.subcategory || item.compliance_subcategory || '—';
                  const isOffZone = item.zoneApplicability === 'off_zone';

                  return (
                    <ReqRow
                      key={item.requirementId || idx}
                      $showSub={activeTab === 'BUILDING_CODE'}
                      style={isOffZone ? { opacity: 0.5 } : undefined}
                      title={isOffZone ? 'Scoped to a zone other than this project — not applicable' : undefined}
                    >
                      <TdRef>
                        {item.article || '—'}
                        {isOffZone && (
                          <span style={{ display: 'block', marginTop: '2px', fontSize: '0.6rem',
                                         fontWeight: 700, textTransform: 'uppercase',
                                         letterSpacing: '0.3px', color: '#9ca3af' }}>
                            Other zone
                          </span>
                        )}
                      </TdRef>
                      <TdText><TdTextInner>{item.requirementText}</TdTextInner></TdText>
                      <TdType>
                        <Badge $type={item.complianceType}>
                          {COMPLIANCE_LABELS[item.complianceType] || complianceLabelFull(item.complianceType)}
                        </Badge>
                      </TdType>
                      {activeTab === 'BUILDING_CODE' && (
                        <TdSub>{item.subcategory || item.compliance_subcategory || '—'}</TdSub>
                      )}
                      <TdSource>
                        {item.sourceDocName && (
                          <DocPill
                            $clickable
                            title={`Open ${item.sourceDocName}`}
                            onClick={() => handleOpenSourceDoc(item)}
                          >
                            <svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                            </svg>
                            {item.sourceDocName.length > 22
                              ? item.sourceDocName.slice(0, 22) + '…'
                              : item.sourceDocName}
                            {item.pageNumber > 0 && (
                              <DocPillPage>p. {item.pageNumber}</DocPillPage>
                            )}
                          </DocPill>
                        )}
                      </TdSource>

                      <TdStatus>
                        {(() => {
                          const vs = item.verificationStatus || item.verification_status || 'unverified';
                          return (
                            <VsBadge $status={vs}>
                              {VS_BADGE_LABELS[vs] || vs}
                            </VsBadge>
                          );
                        })()}
                      </TdStatus>

                      <TdAction>
                        <IconBtn
                          title="View verification history — a running log of every time a compliance scan checked this requirement against your drawings or specs, plus any manual status changes"
                          onClick={() => setHistoryReq(item)}
                        >
                          <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                        </IconBtn>
                        <IconBtn
                          title="Copy requirement text"
                          onClick={() => {
                            navigator.clipboard.writeText(item.requirementText).then(
                              () => message.success('Copied to clipboard'),
                              () => message.error('Failed to copy')
                            );
                          }}
                        >
                          <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                          </svg>
                        </IconBtn>
                      </TdAction>
                    </ReqRow>
                  );
                })}
              </TableContainer>
            )}

            {/* Pagination */}
            {showPagination && (
              <Pagination>
                <PaginationInfo>
                  Page {currentPageNum} · 50 per page
                </PaginationInfo>
                <PaginationControls>
                  <PageBtnWide disabled={prevTokenStack.length === 0} onClick={handlePrevPage}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginRight: 4 }}>
                      <path d="M7.5 9L4.5 6l3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    Previous
                  </PageBtnWide>
                  <PageBtnWide $active disabled={!pageToken} onClick={handleNextPage}>
                    Next
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginLeft: 4 }}>
                      <path d="M4.5 3L7.5 6l-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </PageBtnWide>
                </PaginationControls>
              </Pagination>
            )}
          </TableArea>

          {/* SOURCE DOCS PANEL */}
          <SourcePanel $collapsed={sourcePanelCollapsed}>
            <SourcePanelHeader $collapsed={sourcePanelCollapsed}>
              <SourcePanelTitle $collapsed={sourcePanelCollapsed}>
                Source Documents
              </SourcePanelTitle>
              <PanelCollapseBtn
                onClick={() => setSourcePanelCollapsed(c => !c)}
                title={sourcePanelCollapsed ? 'Expand panel' : 'Collapse panel'}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d={sourcePanelCollapsed ? 'M5 2l4 5-4 5' : 'M9 2L5 7l4 5'}/>
                </svg>
              </PanelCollapseBtn>
            </SourcePanelHeader>

            <SourcePanelBody $collapsed={sourcePanelCollapsed}>
              {/* All */}
              <SourceAllBtn $active={!activeDocFilter} onClick={() => handleDocFilterChange(null)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#999', flexShrink: 0 }}>
                  <rect x="3" y="3" width="7" height="7" rx="1"/>
                  <rect x="14" y="3" width="7" height="7" rx="1"/>
                  <rect x="3" y="14" width="7" height="7" rx="1"/>
                  <rect x="14" y="14" width="7" height="7" rx="1"/>
                </svg>
                <SourceAllLabel $active={!activeDocFilter}>All Documents</SourceAllLabel>
                <SourceAllCount $active={!activeDocFilter}>
                  {activeTab === 'ALL'
                    ? tabCountsUnfiltered.ALL || totalCount
                    : tabCountsUnfiltered[activeTab] || 0}
                </SourceAllCount>
              </SourceAllBtn>

              {activeTab === 'ALL' ? (
              // flat list, no type grouping
              sourceDocs.map(doc => (
                <SourceDocItem
                  key={doc.name}
                  $active={activeDocFilter === doc.name}
                  onClick={() => handleDocFilterChange(doc.name)}
                >
                  <SourceDocIcon $active={activeDocFilter === doc.name}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                  </SourceDocIcon>
                  <SourceDocInfo>
                    <SourceDocName $active={activeDocFilter === doc.name}>{doc.name}</SourceDocName>
                    <SourceDocMeta>{complianceLabelFull(doc.complianceType)}</SourceDocMeta>
                  </SourceDocInfo>
                  <SourceDocCount $active={activeDocFilter === doc.name}>{doc.count}</SourceDocCount>
                </SourceDocItem>
              ))
            ) : (
              // grouped by compliance type for specific tabs
              ['BUILDING_CODE', 'CLIENT_REQUIREMENTS', 'STANDARDS', 'ZONING'].map(type => {
                const typeDocs = sourceDocs.filter(d => d.complianceType === type);
                if (typeDocs.length === 0) return null;
                return (
                  <React.Fragment key={type}>
                    <SourceSectionLabel>{complianceLabelFull(type)}</SourceSectionLabel>
                    {typeDocs.map(doc => (
                      <SourceDocItem
                        key={doc.name}
                        $active={activeDocFilter === doc.name}
                        onClick={() => handleDocFilterChange(doc.name)}
                      >
                        <SourceDocIcon $active={activeDocFilter === doc.name}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                          </svg>
                        </SourceDocIcon>
                        <SourceDocInfo>
                          <SourceDocName $active={activeDocFilter === doc.name}>{doc.name}</SourceDocName>
                          <SourceDocMeta>{complianceLabelFull(doc.complianceType)}</SourceDocMeta>
                        </SourceDocInfo>
                        <SourceDocCount $active={activeDocFilter === doc.name}>{doc.count}</SourceDocCount>
                      </SourceDocItem>
                    ))}
                  </React.Fragment>
                );
              })
            )}

            {sourceDocs.length === 0 && (
              <div style={{ padding: '0.75rem 1rem', fontSize: '0.78rem', color: '#999' }}>
                No source documents yet.
              </div>
            )}
          </SourcePanelBody>
          </SourcePanel>

        </BodyLayout>
      </Page>
      <RequirementsExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={visibleItems}
        projectName={selectedProject?.name}
      />
      <VerificationHistoryPanel
        isOpen={!!historyReq}
        requirementId={historyReq?.requirementId}
        requirement={historyReq}
        projectId={projectId}
        onClose={() => setHistoryReq(null)}
        onOpenSourceDoc={handleOpenSourceDoc}
      />
      {pdfViewer.open && (
        <PdfViewerModal
          pdfUrl={pdfViewer.url}
          initialPage={pdfViewer.initialPage}
          title={pdfViewer.title}
          highlightText={pdfViewer.highlightText}
          onClose={() =>
            setPdfViewer({ open: false, url: '', initialPage: 1, title: '', highlightText: null })
          }
        />
      )}
      {showZonePanel && zonePanelPayload && (
        <ZoneProfilePanel
          payload={zonePanelPayload}
          mode={zoneMode}
          userId={auth?.user?.profile?.sub || auth?.user?.sub || ''}
          onClose={() => setShowZonePanel(false)}
          onDismissStale={() => {
            // The profile 404'd — the doc is no longer linked to this project.
            // Clear the stale pending reminder so the resume banner goes away.
            clearCachedPendingZoneProfile(projectId);
            setPendingZone(null);
            setShowZonePanel(false);
          }}
          onDone={() => {
            if (zoneMode === 'confirm') clearCachedPendingZoneProfile(projectId);
            setShowZonePanel(false);
            // Refresh zone state + the register (refilter updates verdicts now;
            // confirm resumes the worker, results stream in shortly after).
            loadZoneState();
            fetchRequirements({ tab: activeTab, token: null, search: '', docFilter: null });
          }}
        />
      )}
    </>
  );
}